import { X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import Tag from 'components/crm/ui/Tag';
import useCloseOnEscape from 'components/crm/ui/useCloseOnEscape';
import FieldLabel from 'components/styledInput/FieldLabel';
import { LINK_TYPES, linkKey } from 'helper/taskLinks';
import useText from 'hooks/useText';
import LINK_ICONS from '../linkIcons';

// A long list is cut: the search narrows it.
const MAX_PER_TYPE = 20;
const folded = (value) =>
  (value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * « Fiches liées »: the records of a task, as many as needed, each a chip
 * with a cross that removes it. The search, on the name only, opens the
 * options grouped by kind (companies, groups, contacts, temp workers); a
 * temp worker not validated yet says so. `value`: the links; `options`: the
 * links that can be added (helper/taskLinks `linkOptionsOf`).
 */
const TaskLinksField = ({
  label,
  hint,
  placeholder,
  options,
  value,
  onChange,
}) => {
  const { text } = useText('taskForm');
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const box = useRef(null);
  useEffect(() => {
    const close = (event) =>
      box.current && !box.current.contains(event.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  useCloseOnEscape(open, () => setOpen(false));

  const groups = useMemo(() => {
    const taken = new Set(value.map(linkKey));
    const search = folded(query.trim());
    const left = options.filter(
      (option) =>
        !taken.has(linkKey(option)) &&
        (!search || folded(option.label).includes(search)),
    );
    return LINK_TYPES.map((type) => ({
      type,
      items: left
        .filter((option) => option.type === type)
        .slice(0, MAX_PER_TYPE),
    })).filter((group) => group.items.length);
  }, [options, value, query]);

  const add = (option) => {
    onChange([...value, option]);
    setQuery('');
  };
  const remove = (link) =>
    onChange(value.filter((other) => linkKey(other) !== linkKey(link)));
  const notValidated = (link) =>
    link.type === 'tempWorker' && link.validated === false;

  return (
    <div ref={box} className="relative flex flex-col gap-1.5">
      <FieldLabel label={<label htmlFor={id}>{label}</label>} />
      <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-[0.4rem] border px-2 py-1.5 focus-within:border-yellow-500">
        {value.map((link) => {
          const Icon = LINK_ICONS[link.type];
          return (
            <Tag key={linkKey(link)}>
              <span className="flex min-w-0 items-center gap-1">
                <span className="flex shrink-0">
                  <Icon wh={12} color="currentColor" />
                </span>
                <span className="max-w-[12rem] truncate">{link.label}</span>
                {notValidated(link) && (
                  <span className="text-tagOrange-fg">
                    · {text('linksNotValidated')}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={text('linksRemove', { label: link.label })}
                  onClick={() => remove(link)}
                  className="-mr-1 ml-0.5 rounded p-0.5 hover:bg-gray-200"
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </span>
            </Tag>
          );
        })}
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          autoComplete="off"
          value={query}
          placeholder={value.length ? '' : placeholder}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          className="h-7 min-w-[8rem] flex-1 bg-transparent px-1 text-sm placeholder:text-gray-400 focus:outline-none"
        />
      </div>
      {hint && <div className="ft-sm font-normal text-gray-500">{hint}</div>}
      {open && (
        // Opens upwards: the field is the last one of the form.
        <div
          id={`${id}-list`}
          role="listbox"
          aria-label={label}
          className="absolute bottom-full left-0 right-0 z-30 mb-1 max-h-72 overflow-y-auto rounded border bg-white py-1 shadow-2xl motion-safe:animate-menuIn"
        >
          {groups.map((group) => (
            <div
              key={group.type}
              role="group"
              aria-label={text(`linksGroup_${group.type}`)}
            >
              <p className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                {text(`linksGroup_${group.type}`)}
              </p>
              {group.items.map((option) => {
                const Icon = LINK_ICONS[option.type];
                return (
                  <button
                    key={linkKey(option)}
                    type="button"
                    role="option"
                    aria-selected={false}
                    // Keeps the focus in the search, so that several are added in a row.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => add(option)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100"
                  >
                    <span className="flex shrink-0 text-gray-400">
                      <Icon wh={16} color="currentColor" />
                    </span>
                    <span className="min-w-0 truncate text-gray-900">
                      {option.label}
                    </span>
                    {option.sub && (
                      <span className="min-w-0 truncate text-xs text-gray-500">
                        {option.sub}
                      </span>
                    )}
                    {notValidated(option) && (
                      <span className="ml-auto shrink-0">
                        <Tag tone="orange">{text('linksNotValidated')}</Tag>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          {!groups.length && (
            <p className="px-3 py-2 text-sm text-gray-400">
              {text('contactNoResult')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskLinksField;
